import React, { useEffect, useState, useRef } from "react";
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Dialog, DialogContent,
  TextField, CircularProgress, Alert, Stack, Divider, Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import adminApi from "../api/adminApi";

const LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "pt", label: "PT" },
  { code: "es", label: "ES" },
  { code: "fr", label: "FR" },
  { code: "de", label: "DE" },
  { code: "pl", label: "PL" },
];

const EMPTY_LANG = { title: "", summary: "", content: "", metaTitle: "", metaDescription: "", metaKeywords: "" };
const EMPTY_FORM = {
  slug: "", tags: "", published: false, publishedAt: null, scheduledAt: null,
  en: { ...EMPTY_LANG }, pt: { ...EMPTY_LANG }, es: { ...EMPTY_LANG },
  fr: { ...EMPTY_LANG }, de: { ...EMPTY_LANG }, pl: { ...EMPTY_LANG },
};

const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ header: [1, 2, 3, false] }],
    [{ size: ["small", false, "large", "huge"] }],
    [{ color: [] }, { background: [] }],
    ["link", "image", "video"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["blockquote", "code-block"],
    ["clean"],
  ],
};

const PostDialog = ({ open, onClose, post, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [activeLang, setActiveLang] = useState("en");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [htmlMode, setHtmlMode] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    if (post) {
      setForm({
        slug: post.slug || "",
        tags: post.tags?.join(", ") || "",
        published: post.published || false,
        publishedAt: post.publishedAt || null,
        scheduledAt: post.scheduledAt || null,
        en: { ...EMPTY_LANG, ...post.en },
        pt: { ...EMPTY_LANG, ...post.pt },
        es: { ...EMPTY_LANG, ...post.es },
        fr: { ...EMPTY_LANG, ...post.fr },
        de: { ...EMPTY_LANG, ...post.de },
        pl: { ...EMPTY_LANG, ...post.pl },
      });
      setCoverPreview(post.coverImage ? `https://api.whynotcarrental.com/uploads/blog/${post.coverImage}` : null);
    } else {
      setForm(EMPTY_FORM);
      setCoverPreview(null);
    }
    setCoverFile(null);
    setActiveLang("en");
    setScheduleMode(false);
    setScheduledAt(null);
    setError("");
    setHtmlMode(false);
  }, [post, open]);

  const setLangField = (field, value) =>
    setForm((p) => ({ ...p, [activeLang]: { ...p[activeLang], [field]: value } }));

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const submit = async (action) => {
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("slug", form.slug);
      fd.append("tags", JSON.stringify(form.tags.split(",").map((t) => t.trim()).filter(Boolean)));

      if (action === "publish") {
        fd.append("published", "true");
        fd.append("publishedAt", new Date().toISOString());
      } else if (action === "schedule" && scheduledAt) {
        fd.append("published", "false");
        fd.append("scheduledAt", scheduledAt.toISOString());
      } else {
        fd.append("published", "false");
      }

      LANGUAGES.forEach(({ code }) => fd.append(code, JSON.stringify(form[code])));
      if (coverFile) fd.append("coverImage", coverFile);

      if (post?._id) {
        await adminApi.put(`/blog/${post._id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await adminApi.post("/blog", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const lang = form[activeLang];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "95vh" } }}>

        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="h6" fontWeight="bold">{post?._id ? "Edit Blog Post" : "Create Blog Post"}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" size="small" disabled={loading}
              onClick={() => submit("draft")}
              sx={{ backgroundColor: "#1C2541", borderRadius: 5, textTransform: "none" }}>
              Save Draft
            </Button>
            <Button variant="contained" size="small" disabled={loading}
              onClick={() => setScheduleMode((p) => !p)}
              sx={{ backgroundColor: "#1C2541", borderRadius: 5, textTransform: "none" }}>
              Schedule
            </Button>
            <Button variant="contained" size="small" disabled={loading}
              onClick={() => submit("publish")}
              sx={{ backgroundColor: "#1C2541", borderRadius: 5, textTransform: "none" }}>
              {loading ? <CircularProgress size={16} color="inherit" /> : "Publish Now"}
            </Button>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Stack>
        </Box>

        <DialogContent sx={{ px: 3, py: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {scheduleMode && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: "#f9f9f9", borderRadius: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <DateTimePicker label="Schedule publish date" value={scheduledAt} onChange={setScheduledAt}
                slotProps={{ textField: { size: "small" } }} />
              <Button variant="contained" size="small" disabled={!scheduledAt || loading}
                onClick={() => submit("schedule")}
                sx={{ backgroundColor: "#EE8013", borderRadius: 5, textTransform: "none" }}>
                Confirm Schedule
              </Button>
            </Box>
          )}

          {/* Slug + Tags row */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2.5}>
            <TextField label="Slug" size="small" value={form.slug} onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
              disabled={!!post?._id} helperText="URL identifier — e.g. tourist-tax-2026" sx={{ flex: 1 }} />
            <TextField label="Tags (comma separated)" size="small" value={form.tags}
              onChange={(e) => setForm((p) => ({ ...p, tags: e.target.value }))} sx={{ flex: 1 }} />
          </Stack>

          {/* Thumbnail + Language tabs row */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={3} mb={2.5} alignItems="flex-start">
            {/* Thumbnail */}
            <Box>
              <Box onClick={() => fileInputRef.current.click()} sx={{
                width: 140, height: 90, borderRadius: 2, border: "1px solid #ddd",
                backgroundColor: "#f5f5f5", display: "flex", alignItems: "center",
                justifyContent: "center", overflow: "hidden", cursor: "pointer",
                "&:hover": { borderColor: "#1C2541" },
              }}>
                {coverPreview
                  ? <Box component="img" src={coverPreview} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <Typography variant="caption" color="text.secondary">No thumbnail</Typography>}
              </Box>
              <Button size="small" onClick={() => fileInputRef.current.click()}
                sx={{ mt: 0.5, textTransform: "none", fontSize: 12, color: "#1C2541" }}>
                Upload Thumbnail
              </Button>
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleCoverChange} />
            </Box>

            {/* Language tabs */}
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {LANGUAGES.map(({ code, label }) => (
                <Button key={code} onClick={() => setActiveLang(code)} size="small"
                  variant={activeLang === code ? "contained" : "outlined"}
                  sx={{
                    borderRadius: 5, minWidth: 48, textTransform: "none", fontWeight: "bold",
                    backgroundColor: activeLang === code ? "#1C2541" : "transparent",
                    borderColor: "#ccc", color: activeLang === code ? "white" : "#555",
                    "&:hover": { backgroundColor: activeLang === code ? "#263677" : "#f0f0f0", borderColor: "#999" },
                  }}>
                  {label}
                </Button>
              ))}
            </Stack>
          </Stack>

          {/* Language-specific fields */}
          <TextField label={`Title (${activeLang.toUpperCase()}) *`} fullWidth size="small"
            value={lang.title} onChange={(e) => setLangField("title", e.target.value)}
            sx={{ mb: 2 }} />

          <TextField label={`Summary (${activeLang.toUpperCase()})`} fullWidth size="small"
            value={lang.summary} onChange={(e) => setLangField("summary", e.target.value)}
            placeholder="Short summary" sx={{ mb: 2.5 }} />

          <Divider sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">SEO</Typography>
          </Divider>

          <TextField label={`Meta Title (${activeLang.toUpperCase()})`} fullWidth size="small"
            value={lang.metaTitle} onChange={(e) => setLangField("metaTitle", e.target.value)}
            sx={{ mb: 2 }} />
          <TextField label={`Meta Description (${activeLang.toUpperCase()})`} fullWidth size="small"
            value={lang.metaDescription} onChange={(e) => setLangField("metaDescription", e.target.value)}
            sx={{ mb: 2 }} />
          <TextField label={`Meta Keywords (${activeLang.toUpperCase()})`} fullWidth size="small"
            value={lang.metaKeywords} onChange={(e) => setLangField("metaKeywords", e.target.value)}
            placeholder="keyword1, keyword2" sx={{ mb: 2.5 }} />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Divider sx={{ flex: 1, mr: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                Content ({activeLang.toUpperCase()})
              </Typography>
            </Divider>
            <Button
              size="small"
              variant={htmlMode ? "contained" : "outlined"}
              onClick={() => setHtmlMode((p) => !p)}
              sx={{
                textTransform: "none", borderRadius: 5, fontSize: 11, px: 1.5,
                backgroundColor: htmlMode ? "#1C2541" : "transparent",
                borderColor: "#ccc", color: htmlMode ? "white" : "#555",
                flexShrink: 0,
              }}
            >
              {htmlMode ? "Visual" : "&lt;/&gt; HTML"}
            </Button>
          </Box>

          {htmlMode ? (
            <TextField
              fullWidth
              multiline
              minRows={12}
              value={lang.content}
              onChange={(e) => setLangField("content", e.target.value)}
              placeholder="<p>Write your HTML here...</p>"
              inputProps={{ style: { fontFamily: "monospace", fontSize: 13, lineHeight: 1.6 } }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          ) : (
            <Box sx={{ "& .ql-container": { minHeight: 200, fontSize: 14 }, "& .ql-editor": { minHeight: 200 } }}>
              <ReactQuill theme="snow" value={lang.content} onChange={(val) => setLangField("content", val)}
                modules={QUILL_MODULES} placeholder="Write your amazing post..." />
            </Box>
          )}

          {/* Bottom actions */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
            <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button variant="contained" onClick={() => submit("draft")} disabled={loading}
              sx={{ backgroundColor: "#1C2541", borderRadius: 5, textTransform: "none" }}>
              {loading ? <CircularProgress size={18} color="inherit" /> : "Save Draft"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </LocalizationProvider>
  );
};

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await adminApi.get("/blog/all");
    setPosts(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    await adminApi.delete(`/blog/${id}`);
    load();
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold" color="#1C2541">Blog Manager</Typography>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setSelected(null); setDialogOpen(true); }}
          sx={{ backgroundColor: "#1C2541", borderRadius: 2 }}>
          New Post
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f0f2f5" }}>
            <TableRow>
              <TableCell><strong>Title (EN)</strong></TableCell>
              <TableCell><strong>Slug</strong></TableCell>
              <TableCell><strong>Languages</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {posts.map((p) => (
              <TableRow key={p._id} hover>
                <TableCell>{p.en?.title || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                <TableCell><Typography variant="body2" fontFamily="monospace">{p.slug}</Typography></TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {LANGUAGES.map(({ code }) => (
                      p[code]?.title ? <Chip key={code} label={code.toUpperCase()} size="small" sx={{ fontSize: 10, height: 20 }} /> : null
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    label={p.scheduledAt && !p.published ? "Scheduled" : p.published ? "Published" : "Draft"}
                    color={p.published ? "success" : p.scheduledAt ? "warning" : "default"}
                    size="small" />
                </TableCell>
                <TableCell><Typography variant="caption">{new Date(p.createdAt).toLocaleDateString()}</Typography></TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => { setSelected(p); setDialogOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(p._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {posts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" py={3}>No posts yet</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PostDialog open={dialogOpen} onClose={() => setDialogOpen(false)} post={selected} onSaved={load} />
    </Box>
  );
};

export default BlogManager;
